import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowRight, FiCheck, FiShield, FiZap,
  FiMinus, FiChevronDown, FiStar, FiBriefcase,
} from 'react-icons/fi';
import { toast } from 'sonner';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import billingService from '../../services/billingService';

/* ═══════════════════════════════════════════════════════════
   PLAN DATA
═══════════════════════════════════════════════════════════ */
const plans = [
  {
    id: 'free',
    name: 'Free',
    audience: 'For individuals getting started',
    price: '₹0',
    annual: null,
    savings: null,
    cadence: 'forever',
    description: 'Perfect for students, personal productivity, and trying out Tasklina.',
    cta: 'Get started free',
    checkoutPlan: null,
    features: [
      'Unlimited tasks', 'Up to 3 active projects', 'Up to 3 members per project',
      'Calendar View', 'Milestones and task comments', 'Priorities and labels',
      '7 days activity history', 'Project-based role access',
      '50 Lina AI credits / month', '100 MB storage · 10 MB max file',
      'Community support', 'Apps at full price',
    ],
    featureGroups: [
      { label: 'Work Management', items: ['Unlimited tasks', '3 active projects', '3 members / project', 'Milestones & comments', 'Priorities & labels'] },
      { label: 'Views', items: ['List view', 'Calendar view'] },
      { label: 'Lina AI', items: ['50 AI credits / month', 'All 180+ AI actions included'] },
      { label: 'Storage & Support', items: ['100 MB storage · 10 MB files', 'Community support', 'Apps at full price'] },
    ],
  },
  {
    id: 'personal_pro',
    name: 'Pro',
    audience: 'For freelancers and power users',
    price: '₹299',
    annual: '₹2,999 / year',
    savings: 'Save ₹588',
    cadence: 'per month',
    description: 'Professional project management for solo work, client delivery, and advanced views.',
    cta: 'Start Pro',
    checkoutPlan: 'personal_pro',
    features: [
      'Everything in Free, plus:', 'Unlimited projects', 'Up to 50 members per project',
      'Unlimited file attachments', 'Task dependencies',
      'Kanban Board · Timeline / Gantt · Sticky Notes', '180 days activity history',
      'Project discussions and team messaging', 'Real-time email notifications', 'Google Calendar sync',
      '1,000 Lina AI credits / month', 'API keys · Webhooks · Integrations',
      '5 GB storage · 100 MB max file', 'Email support · Discounted add-ons',
    ],
    featureGroups: [
      { label: 'Everything in Free, plus', isInherit: true, items: ['Unlimited projects', '50 members / project', 'Unlimited attachments', 'Task dependencies', '180 days history'] },
      { label: 'Views & Productivity', items: ['Kanban Board', 'Timeline / Gantt', 'Sticky Notes workspace', 'Google Calendar sync'] },
      { label: 'Collaboration', items: ['Project discussions & messaging', 'Real-time email notifications'] },
      { label: 'Lina AI', items: ['1,000 AI credits / month'] },
      { label: 'Storage & Support', items: ['5 GB · 100 MB files', 'Email support', 'Discounted add-ons'] },
    ],
  },
  {
    id: 'team',
    name: 'Team',
    audience: 'For startups, agencies, and growing teams',
    price: '₹399',
    annual: '₹299 / user / month annually',
    savings: 'Save 25%',
    cadence: 'per user / month',
    description: 'Shared workspace, org roles, and team reports for day-to-day collaboration.',
    cta: 'Start 14-day free trial',
    checkoutPlan: 'team',
    highlighted: true,
    badge: 'Most Popular',
    features: [
      'Everything in Pro, plus:', 'Organization workspace', 'Unlimited teams · Up to 5 departments',
      'Org-level RBAC + department role hierarchy', 'Shared team calendars',
      'Guest and client access', 'Multi-factor authentication',
      'Domain-based organization joining', 'Basic audit logs and security logs',
      'Team analytics and reports', 'Unlimited activity history',
      '2,500 workspace AI credits + 250 / seat / month',
      'Custom branding and white-labeling', '25 GB storage · 100 MB max file',
      'Priority email and chat support · Discounted add-ons',
    ],
    featureGroups: [
      { label: 'Everything in Pro, plus', isInherit: true, items: ['Organization workspace', 'Unlimited teams · 5 departments', 'Guest & client access', 'Shared team calendars'] },
      { label: 'Roles & Access', items: ['Org-level RBAC', 'Department role hierarchy', 'Domain-based org joining'] },
      { label: 'Security & Analytics', items: ['Multi-factor authentication', 'Basic audit logs & security logs', 'Team analytics & reports', 'Unlimited activity history'] },
      { label: 'Lina AI', items: ['2,500 workspace + 250 / seat credits'] },
      { label: 'Storage & Support', items: ['25 GB · 100 MB files', 'Custom branding', 'Priority support'] },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    audience: 'For scaling organizations',
    price: '₹899',
    annual: '₹699 / user / month annually',
    savings: 'Save 22%',
    cadence: 'per user / month',
    description: 'Governance, audit trails, and high-volume AI capacity for scaling ops teams.',
    cta: 'Start 14-day free trial',
    checkoutPlan: 'business',
    features: [
      'Everything in Team, plus:', 'Unlimited departments', 'Custom permission templates',
      'Full audit logs with 1-year retention', 'Full data export',
      '10,000 workspace AI credits + 1,000 / seat / month', 'AI priority processing queue',
      'Custom email notification templates', 'Dedicated onboarding assistance',
      '50 GB storage · 250 MB max file', 'Priority support included',
    ],
    featureGroups: [
      { label: 'Everything in Team, plus', isInherit: true, items: ['Unlimited departments', 'Custom permission templates', 'Advanced workflow controls'] },
      { label: 'Governance', items: ['Full audit logs + 1-year retention', 'Full data export', 'Custom email templates'] },
      { label: 'Lina AI', items: ['10,000 workspace + 1,000 / seat credits', 'AI priority processing queue'] },
      { label: 'Storage & Support', items: ['50 GB · 250 MB files', 'Dedicated onboarding', 'Priority support included'] },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    audience: 'Built for large organizations and regulated industries',
    price: 'Custom',
    annual: null,
    savings: null,
    cadence: 'annual contract',
    description: 'Security, compliance, procurement, SLA, and fully custom scale.',
    cta: 'Talk to sales',
    checkoutPlan: null,
    features: [
      'Everything in Business, plus:', 'Single Sign-On (SAML / OIDC)', 'SCIM provisioning',
      'IP restrictions · Domain lock policies', 'Customer Success Manager',
      'Enterprise onboarding and migration assistance', 'Contractual SLA',
      'Custom storage, API and AI limits', 'Volume-based AI contracts',
    ],
    featureGroups: [
      { label: 'Everything in Business, plus', isInherit: true, items: ['Single Sign-On (SAML / OIDC)', 'SCIM provisioning', 'IP restrictions', 'Domain lock policies'] },
      { label: 'Dedicated Success', items: ['Customer Success Manager', 'Enterprise onboarding & migration', 'Contractual SLA'] },
      { label: 'Custom Limits', items: ['Custom storage, API & AI limits', 'Volume-based AI contracts'] },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════
   COMPARISON TABLE — grouped by category
═══════════════════════════════════════════════════════════ */
const comparisonGroups = [
  {
    category: 'Project & Task Management',
    rows: [
      { label: 'Tasks per Project', itemId: 'max_tasks_per_project', values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited', 'Custom'] },
      { label: 'Subtasks per Task', itemId: 'max_subtasks_per_task', values: ['10', 'Unlimited', 'Unlimited', 'Unlimited', 'Custom'] },
      { label: 'Attachments per Task', itemId: 'max_attachments_per_task', values: ['5', 'Unlimited', 'Unlimited', 'Unlimited', 'Custom'] },
      { label: 'Active Projects', itemId: 'max_projects', values: ['3', 'Unlimited', 'Unlimited', 'Unlimited', 'Custom'] },
      { label: 'Members per Project', itemId: 'max_members_per_project', values: ['3', '50', 'Unlimited', 'Unlimited', 'Custom'] },
      { label: 'Task Dependencies', itemId: 'task_dependencies', values: ['—', '✓', '✓', '✓', '✓'] },
      { label: 'Activity History', itemId: 'activity_retention_days', unit: 'days', values: ['7 days', '180 days', 'Unlimited', 'Unlimited', 'Custom'] },
    ],
  },
  {
    category: 'Views & Workspace',
    rows: [
      { label: 'List & Calendar View', values: ['✓', '✓', '✓', '✓', '✓'] },
      { label: 'Kanban Board', itemId: 'kanban_view', values: ['—', '✓', '✓', '✓', '✓'] },
      { label: 'Timeline / Gantt', itemId: 'timeline_view', values: ['—', '✓', '✓', '✓', '✓'] },
      { label: 'Sticky Notes', itemId: 'sticky_notes', values: ['—', '✓', '✓', '✓', '✓'] },
      { label: 'Google Calendar Sync', itemId: 'google_calendar_sync', values: ['—', '✓', '✓', '✓', '✓'] },
    ],
  },
  {
    category: 'Team & Organization',
    rows: [
      { label: 'Organization Workspace', values: ['—', '—', '✓', '✓', '✓'] },
      { label: 'Departments', itemId: 'max_departments', values: ['—', '—', '5', 'Unlimited', 'Custom'] },
      { label: 'Guest / Client Access', itemId: 'guest_access', values: ['—', '—', '✓', '✓', '✓'] },
      { label: 'Analytics & Reports', values: ['—', '—', '✓', '✓', '✓'] },
      { label: 'White Label / Branding', itemId: 'custom_branding', values: ['—', '—', '✓', '✓', '✓'] },
    ],
  },
  {
    category: 'Lina AI',
    rows: [
      { label: 'AI Credits / Month', itemId: 'max_ai_queries', values: ['50', '1,000', '2,500 + 250/seat', '10,000 + 1,000/seat', 'Custom'] },
      { label: 'All AI Modules (180+ actions)', values: ['✓', '✓', '✓', '✓', '✓'] },
      { label: 'AI Priority Queue', itemId: 'ai_priority_queue', values: ['—', '—', '—', '✓', '✓'] },
    ],
  },
  {
    category: 'Storage',
    rows: [
      { label: 'Workspace Storage', itemId: 'max_storage_gb', unit: 'storage', values: ['100 MB', '5 GB', '25 GB', '50 GB', 'Custom'] },
      { label: 'Max File Size', itemId: 'max_file_size_mb', unit: 'MB', values: ['10 MB', '100 MB', '100 MB', '250 MB', 'Custom'] },
    ],
  },
  {
    category: 'Developer & Integrations',
    rows: [
      { label: 'API Keys', itemId: 'max_api_keys', values: ['—', '1', '5', '20', 'Custom'] },
      { label: 'Outbound Webhooks', itemId: 'max_webhooks', values: ['—', '1', '5', '20', 'Custom'] },
      { label: 'Third-party Integrations', itemId: 'max_integrations', values: ['—', '2', '5', '20', 'Custom'] },
      { label: 'Automation Runs', itemId: 'automation_runs', values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'] },
      { label: 'Webhook Deliveries', itemId: 'webhook_deliveries', values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'] },
      { label: 'Integration Syncs', itemId: 'integration_sync_runs', values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'] },
    ],
  },
  {
    category: 'Security & Access',
    rows: [
      { label: 'Project RBAC', values: ['✓', '✓', '✓', '✓', '✓'] },
      { label: 'Org Level RBAC', values: ['—', '—', '✓', '✓', '✓'] },
      { label: 'MFA / 2FA', itemId: 'mfa', values: ['—', '—', '✓', '✓', 'Enforced'] },
      { label: 'Domain Mapping', values: ['—', '—', '✓', '✓', '✓'] },
      { label: 'Audit Logs', itemId: 'audit_logs', values: ['—', '—', 'Basic', 'Full (1 yr)', 'Enterprise'] },
      { label: 'SSO (SAML / OIDC)', itemId: 'sso', values: ['—', '—', '—', '—', '✓'] },
    ],
  },
  {
    category: 'Support',
    rows: [
      { label: 'Support Channel', values: ['Community', 'Email', 'Priority email', 'Priority+', 'SLA + CSM'] },
      { label: 'Dedicated Onboarding', values: ['—', '—', '—', '✓', '✓'] },
    ],
  },
  {
    category: 'Add-ons & Modules',
    rows: [
      { label: 'Finance Module', values: ['—', '—', 'Separate app', 'Separate app', 'Custom'] },
      { label: 'Apps Pricing', values: ['Full price', 'Discounted', 'Discounted', 'Discounted', 'Custom'] },
      { label: 'Extra AI Credits', values: ['—', 'Add-on', 'Add-on', 'Add-on', 'Custom'] },
      { label: 'Extra Storage', values: ['—', 'Add-on', 'Add-on', 'Add-on', 'Custom'] },
    ],
  },
];

const COMPARISON_PLAN_IDS = ['free', 'personal_pro', 'team', 'business', 'enterprise'];
const FAIR_USE_ITEM_IDS = new Set(['automation_runs', 'webhook_deliveries', 'integration_sync_runs']);

function formatCatalogAllowance(itemId, value, planId, unit) {
  if (FAIR_USE_ITEM_IDS.has(itemId)) return 'Unlimited';
  if (itemId === 'mfa' && planId === 'enterprise' && value) return 'Enforced';
  if (itemId === 'audit_logs' && value) {
    if (planId === 'team') return 'Basic';
    if (planId === 'business') return 'Full (1 yr)';
    if (planId === 'enterprise') return 'Enterprise';
  }
  if (planId === 'enterprise' && typeof value !== 'boolean') return 'Custom';
  if (typeof value === 'boolean') return value ? '✓' : '—';
  if (value === null || value === -1) return 'Unlimited';
  if (value === undefined) return null;
  if (Number(value) === 0) return '—';
  if (itemId === 'max_ai_queries' && planId === 'team') return `${Number(value).toLocaleString('en-IN')} + 250/seat`;
  if (itemId === 'max_ai_queries' && planId === 'business') return `${Number(value).toLocaleString('en-IN')} + 1,000/seat`;
  if (unit === 'storage') return Number(value) < 1 ? `${Math.round(Number(value) * 1000)} MB` : `${Number(value).toLocaleString('en-IN')} GB`;
  if (unit) return `${Number(value).toLocaleString('en-IN')} ${unit}`;
  return Number(value).toLocaleString('en-IN');
}

function resolveComparisonGroups(catalog) {
  if (!catalog?.plans?.length) return comparisonGroups;
  const plansById = new Map(catalog.plans.map((plan) => [plan.id, plan]));

  return comparisonGroups.map((group) => ({
    ...group,
    rows: group.rows.map((row) => {
      if (!row.itemId) return row;
      const resolvedValues = COMPARISON_PLAN_IDS.map((planId, index) => {
        const value = plansById.get(planId)?.limits?.[row.itemId];
        return formatCatalogAllowance(row.itemId, value, planId, row.unit) ?? row.values[index];
      });
      return { ...row, values: resolvedValues };
    })
  }));
}

/* ═══════════════════════════════════════════════════════════
   FAQ DATA
═══════════════════════════════════════════════════════════ */
const faqs = [
  { q: 'Is there a free trial for paid plans?', a: 'Yes. Pro, Team, and Business each include a 14-day free trial — no credit card required. Enterprise gets a 30-day trial with a sales-assisted onboarding call.' },
  { q: 'Can I upgrade or downgrade at any time?', a: 'Absolutely. Upgrades take effect immediately with pro-rated billing. Downgrades apply at the end of your current billing cycle — your access continues until then.' },
  { q: 'What happens when I run out of Lina AI credits?', a: "You'll receive a notification and can purchase add-on credit packs from Billing at any time. Credits reset every calendar month regardless of when you bought the pack." },
  { q: 'What is the difference between Team and Business?', a: 'Team gives you an organization workspace, RBAC, departments, and shared calendars. Business adds custom permission templates, full audit logs with 1-year retention, full data export, and significantly higher AI credit limits.' },
  { q: 'Are API requests and automations metered?', a: 'No. API requests, automation runs, webhooks, and integration syncs are unlimited across every plan under fair use. Only Lina AI uses monthly credits — and all 180+ AI actions are available even on Free.' },
  { q: 'Do annual plans auto-renew?', a: 'Yes. Annual plans auto-renew 30 days before the cycle ends. You will receive an email reminder and can cancel or change plans from your billing settings at any time.' },
];

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
function getIpBasedRegion() {
  try {
    const cachedCountry = localStorage.getItem('tasklina_ip_country');
    if (cachedCountry) {
      return cachedCountry === 'IN' ? 'IN' : 'GLOBAL';
    }
    return 'IN'; // default fallback until IP loads
  } catch (e) {
    return 'IN';
  }
}

const staticPlanPricing = {
  free: {
    IN: { monthly: 0, annual: 0, currency: 'inr', locale: 'en-IN' },
    GLOBAL: { monthly: 0, annual: 0, currency: 'usd', locale: 'en-US' }
  },
  personal_pro: {
    IN: { monthly: 299, annual: 2999, currency: 'inr', locale: 'en-IN', savings: 'Save ₹588' },
    GLOBAL: { monthly: 4.99, annual: 47.99, currency: 'usd', locale: 'en-US', savings: 'Save 20%' }
  },
  team: {
    IN: { monthly: 399, annual: 3588, currency: 'inr', locale: 'en-IN', savings: 'Save 25%' },
    GLOBAL: { monthly: 7.99, annual: 83.99, currency: 'usd', locale: 'en-US', savings: 'Save 12.5%' }
  },
  business: {
    IN: { monthly: 899, annual: 8388, currency: 'inr', locale: 'en-IN', savings: 'Save 22%' },
    GLOBAL: { monthly: 17.99, annual: 179.99, currency: 'usd', locale: 'en-US', savings: 'Save 16.6%' }
  },
  enterprise: {
    IN: { monthly: null, annual: null, currency: 'inr', locale: 'en-IN' },
    GLOBAL: { monthly: null, annual: null, currency: 'usd', locale: 'en-US' }
  }
};

function formatMoney(value, currency = 'inr', locale = 'en-IN') {
  if (value == null) return 'Custom';
  const usesDecimals = String(currency).toLowerCase() !== 'inr' && !Number.isInteger(Number(value));
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: String(currency).toUpperCase(),
    minimumFractionDigits: usesDecimals ? 2 : 0,
    maximumFractionDigits: usesDecimals ? 2 : 0,
  }).format(Number(value));
}

function toDisplayPlans(catalog, selectedCycle) {
  const activeRegion = catalog?.priceRegion || getIpBasedRegion();
  const activeCurrency = catalog?.currency || (activeRegion === 'IN' ? 'inr' : 'usd');
  const activeLocale = catalog?.locale || (activeRegion === 'IN' ? 'en-IN' : 'en-US');

  return plans.map((fallbackPlan) => {
    const catalogPlan = catalog?.plans?.find((p) => p.id === fallbackPlan.id);
    const regionPricing = staticPlanPricing[fallbackPlan.id]?.[activeRegion] || staticPlanPricing[fallbackPlan.id]?.IN;
    
    const monthlyPrice = catalogPlan?.price?.monthly ?? regionPricing.monthly;
    const annualPrice = catalogPlan?.price?.annual ?? regionPricing.annual;
    const currency = catalogPlan?.currency || activeCurrency;
    const locale = catalogPlan?.locale || activeLocale;
    const savingsLabel = regionPricing?.savings || null;

    const isOrgPlan = ['team', 'business'].includes(fallbackPlan.id);

    let displayPriceLabel = '';
    let displayCadenceLabel = '';
    let displayAnnualLabel = '';

    if (fallbackPlan.id === 'free') {
      displayPriceLabel = formatMoney(0, currency, locale);
      displayCadenceLabel = 'forever';
      displayAnnualLabel = '';
    } else if (fallbackPlan.id === 'enterprise') {
      displayPriceLabel = 'Custom';
      displayCadenceLabel = 'annual contract';
      displayAnnualLabel = '';
    } else {
      if (selectedCycle === 'monthly') {
        displayPriceLabel = formatMoney(monthlyPrice, currency, locale);
        displayCadenceLabel = isOrgPlan ? 'per user / month' : 'per month';
        
        if (annualPrice) {
          displayAnnualLabel = `or ${formatMoney(annualPrice, currency, locale)} / year`;
        }
      } else {
        displayPriceLabel = formatMoney(annualPrice, currency, locale);
        displayCadenceLabel = isOrgPlan ? 'per user / year' : 'per year';
        
        displayAnnualLabel = `billed annually as ${formatMoney(annualPrice, currency, locale)} / year`;
      }
    }

    return {
      ...fallbackPlan,
      name: catalogPlan?.name || fallbackPlan.name,
      audience: fallbackPlan.audience || catalogPlan?.tagline,
      price: displayPriceLabel,
      cadence: displayCadenceLabel,
      annual: displayAnnualLabel,
      savings: savingsLabel,
      description: catalogPlan?.description || fallbackPlan.description,
      checkoutPlan: fallbackPlan.id === 'free' || fallbackPlan.id === 'enterprise' ? null : fallbackPlan.id,
      highlighted: catalogPlan?.recommended ?? fallbackPlan.highlighted,
      features: catalogPlan?.features?.length ? catalogPlan.features : fallbackPlan.features,
    };
  });
}

/* ═══════════════════════════════════════════════════════════
   COMPARISON TABLE — cell renderer
═══════════════════════════════════════════════════════════ */
function CellValue({ value, isHighlighted }) {
  if (value === '✓') {
    return (
      <span className="flex justify-center">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${isHighlighted ? 'bg-indigo-100 dark:bg-indigo-900/60' : 'bg-emerald-50 dark:bg-emerald-900/30'}`}>
          <FiCheck className={`h-4 w-4 ${isHighlighted ? 'text-indigo-600 dark:text-indigo-300' : 'text-emerald-600 dark:text-emerald-300'}`} />
        </span>
      </span>
    );
  }
  if (value === '—') {
    return (
      <span className="flex justify-center">
        <FiMinus className="h-5 w-5 text-gray-300 dark:text-gray-500" />
      </span>
    );
  }
  return (
    <span className={`block text-center text-sm font-semibold ${isHighlighted ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
      {value}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   PLAN CARD
═══════════════════════════════════════════════════════════ */
function PlanCard({ plan, onSelect, selectedCycle }) {
  const isHighlighted = !!plan.highlighted;
  const isEnterprise = plan.id === 'enterprise';
  const groups = plan.featureGroups || [];
  const planAccent = plan.id === 'personal_pro'
    ? 'indigo'
    : plan.id === 'team'
      ? 'violet'
      : plan.id === 'business'
        ? 'emerald'
        : plan.id === 'enterprise'
          ? 'amber'
          : 'sky';

  return (
    <div
      className={`relative flex flex-col rounded-2xl h-full transition-all duration-200
        ${isHighlighted
          ? 'border-2 border-indigo-500 dark:border-indigo-500 shadow-2xl shadow-indigo-100 dark:shadow-indigo-900/40 bg-white dark:bg-gray-900'
          : isEnterprise
            ? 'border border-gray-800 bg-gray-950 dark:bg-black'
            : 'border border-gray-200/90 dark:border-gray-800 bg-white dark:bg-gray-900/90 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-lg hover:shadow-gray-100/80 dark:hover:shadow-none'
        }`}
    >
      {/* Top gradient accent bar for highlighted plan */}
      {isHighlighted && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 rounded-t-2xl bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500" />
      )}

      {/* Most Popular badge */}
      {plan.badge && isHighlighted && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-1.5 text-xs font-bold text-white shadow-lg shadow-indigo-500/30">
            <FiStar className="h-3 w-3" />
            {plan.badge}
          </span>
        </div>
      )}

      <div className="flex flex-1 flex-col p-6 pt-8">

        {/* Plan name + audience */}
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${isEnterprise ? 'text-white' : 'text-gray-950 dark:text-white'}`}>
            {plan.name}
          </h2>
          <p className={`mt-1 min-h-[44px] text-[15px] leading-6 ${isEnterprise ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>
            {plan.audience}
          </p>
        </div>

        {/* Price block */}
        <div className="mt-6 min-h-[196px] border-b border-gray-100 pb-6 dark:border-gray-800">
          <div className="flex items-end gap-2">
            <span className={`text-[3rem] font-extrabold leading-none tracking-tight ${isEnterprise ? 'text-white' : 'text-gray-950 dark:text-white'}`}>
              {plan.price}
            </span>
            {plan.cadence !== 'annual contract' && plan.cadence !== 'forever' && (
              <span className={`mb-1 text-base leading-6 ${isEnterprise ? 'text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                {plan.cadence}
              </span>
            )}
            {plan.cadence === 'forever' && (
              <span className="mb-1 text-base text-gray-500 dark:text-gray-400">/ forever</span>
            )}
          </div>

          {plan.annual && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`text-sm ${isEnterprise ? 'text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                {plan.annual}
              </span>
              {plan.savings && selectedCycle === 'monthly' && (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800">
                  {plan.savings}
                </span>
              )}
            </div>
          )}
          {plan.cadence === 'annual contract' && (
            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">Annual contract · Contact sales</p>
          )}

          <p className={`mt-3 text-[15px] leading-7 ${isEnterprise ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>
            {plan.description}
          </p>
        </div>

        {/* CTA button */}
        <div className="py-5">
          <button
            type="button"
            onClick={() => onSelect(plan)}
            className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900
              ${isHighlighted
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200 dark:shadow-indigo-900/40 focus:ring-indigo-500'
                : isEnterprise
                  ? 'bg-white/10 text-white hover:bg-white/20 ring-1 ring-white/20 focus:ring-white/30'
                  : 'bg-gray-950 text-white hover:bg-gray-800 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400 focus:ring-gray-500 dark:focus:ring-indigo-400'
              }`}
          >
            {plan.cta}
            <FiArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Feature groups */}
        <div className="flex-1 space-y-5 border-t border-gray-100 pt-5 dark:border-gray-800">
          {groups.map((group) => (
            <div key={group.label}>
              <p className={`mb-2.5 text-[11px] font-bold uppercase tracking-[0.14em]
                ${group.isInherit
                  ? isEnterprise ? 'text-indigo-300' : 'text-indigo-600 dark:text-indigo-300'
                  : isEnterprise
                    ? 'text-amber-300/80'
                    : planAccent === 'emerald'
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : planAccent === 'violet'
                        ? 'text-violet-700 dark:text-violet-300'
                        : planAccent === 'indigo'
                          ? 'text-indigo-700 dark:text-indigo-300'
                          : 'text-sky-700 dark:text-sky-300'
                }`}>
                {group.label}
              </p>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <li key={item} className={`flex items-start gap-2.5 text-sm leading-6 ${isEnterprise ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>
                    <FiCheck className={`mt-0.5 h-4 w-4 flex-shrink-0
                      ${isHighlighted ? 'text-indigo-500 dark:text-indigo-400' : isEnterprise ? 'text-emerald-400' : 'text-emerald-500 dark:text-emerald-400'}`}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FAQ ITEM — accordion
═══════════════════════════════════════════════════════════ */
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-colors duration-150 ${open ? 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900' : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:border-gray-300 dark:hover:border-gray-700'}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-base font-semibold text-gray-900 dark:text-white">{q}</span>
        <span className={`flex-shrink-0 rounded-full p-1 transition-all duration-200 ${open ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
          <FiChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-[15px] leading-7 text-gray-700 dark:text-gray-300">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PRICING PAGE
═══════════════════════════════════════════════════════════ */
const Pricing = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    billingService.getPublicPlanCatalog().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const displayPlans = useMemo(() => toDisplayPlans(catalog, selectedCycle), [catalog, selectedCycle]);
  const resolvedComparisonGroups = useMemo(() => resolveComparisonGroups(catalog), [catalog]);

  const handlePlanSelect = (plan) => {
    if (plan.id === 'enterprise') { navigate('/contact'); return; }
    if (plan.id === 'free') {
      navigate(currentUser ? (currentOrganization ? '/org/dashboard' : '/dashboard') : '/auth/register-options');
      return;
    }
    if (!currentUser) { navigate('/auth/register-options'); return; }
    if (plan.id === 'personal_pro' && currentOrganization) {
      toast.info('Pro is for personal workspaces. Use Team or Business for organization billing.');
      return;
    }
    if (plan.checkoutPlan) {
      if (plan.id === 'personal_pro') {
        navigate(`/billing/checkout?plan=${plan.checkoutPlan}&cycle=${selectedCycle}`);
        return;
      }
      if (!currentOrganization) {
        navigate(`/auth/register-organization?plan=${plan.checkoutPlan}&cycle=${selectedCycle}`);
        return;
      }
      navigate(`/org/billing/checkout?plan=${plan.checkoutPlan}&cycle=${selectedCycle}`);
      return;
    }
    navigate('/contact');
  };

  // Column index for the highlighted (Team) plan in comparison table
  const TEAM_COL = 2;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <PublicPageHeader />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white dark:bg-gray-950">
        {/* Soft atmospheric glow */}
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
          <div className="absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-indigo-400/10 blur-3xl dark:bg-indigo-500/8" />
        </div>

        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          {/* Trust badge pills */}
          <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
            {[
              {
                icon: FiZap,
                label: 'Unlimited automations on every plan',
                className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/60 dark:bg-amber-500/15 dark:text-amber-200',
                iconClass: 'text-amber-500 dark:text-amber-300',
              },
              {
                icon: FiCheck,
                label: 'All 180+ AI actions on Free',
                className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-500/15 dark:text-emerald-200',
                iconClass: 'text-emerald-500 dark:text-emerald-300',
              },
              {
                icon: FiShield,
                label: '14-day free trial — no card needed',
                className: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-700/60 dark:bg-indigo-500/15 dark:text-indigo-200',
                iconClass: 'text-indigo-500 dark:text-indigo-300',
              },
            ].map(({ icon: Icon, label, className, iconClass }) => (
              <span
                key={label}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium shadow-sm ${className}`}
              >
                <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
                {label}
              </span>
            ))}
          </div>

          {/* Headline */}
          <h1 className="text-5xl font-extrabold tracking-tight text-gray-950 dark:text-white sm:text-6xl">
            Find the right plan{' '}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              for your work
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-xl leading-relaxed text-gray-600 dark:text-gray-300">
            Whether you're managing personal projects, collaborating with a small team, or running operations across an entire organization — Tasklina scales with your workflow.
          </p>

          {/* Billing cycle toggle */}
          <div className="mt-10 flex items-center justify-center gap-4">
            <div className="inline-flex items-center rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-gray-800 dark:bg-gray-900">
              {[
                { key: 'monthly', label: 'Monthly' },
                { key: 'annual', label: 'Annual' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedCycle(key)}
                  className={`relative rounded-lg px-6 py-2.5 text-sm font-semibold transition-all duration-200
                    ${selectedCycle === key
                      ? 'bg-white text-gray-950 shadow-sm dark:bg-gray-800 dark:text-white'
                      : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {selectedCycle === 'annual' ? (
              <span className="rounded-full bg-emerald-50 px-3.5 py-1.5 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800">
                Save up to 25%
              </span>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-300">Switch to annual to save more</span>
            )}
          </div>
        </div>
      </section>

      {/* ── PLAN CARDS GRID ──────────────────────────────────── */}
      <section className="mx-auto max-w-[1420px] px-6 pb-20 pt-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 xl:items-start">
          {displayPlans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} onSelect={handlePlanSelect} selectedCycle={selectedCycle} />
          ))}
        </div>
      </section>

      {/* ── KEY DIFFERENTIATORS ──────────────────────────────── */}
      <section className="border-y border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/40">
        <div className="mx-auto grid max-w-5xl gap-8 px-6 py-14 sm:grid-cols-3">
          {[
            {
              icon: FiZap,
              title: 'No API or automation cap',
              body: 'API requests, automation runs, webhooks, and integration syncs are unlimited across all plans. Only Lina AI uses monthly credits.',
              accent: 'bg-amber-50 text-amber-500 dark:bg-amber-900/20 dark:text-amber-400',
            },
            {
              icon: FiStar,
              title: 'Every AI action on every plan',
              body: 'All 180+ Lina AI actions across 24 modules are available from Free onwards. Credits control volume — not capability.',
              accent: 'bg-indigo-50 text-indigo-500 dark:bg-indigo-900/20 dark:text-indigo-400',
            },
            {
              icon: FiShield,
              title: 'Enterprise is always sales-led',
              body: 'SSO, SCIM, domain lock, SLA, and procurement are handled through sales — no self-serve lock-in or surprise billing.',
              accent: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/20 dark:text-emerald-400',
            },
          ].map(({ icon: Icon, title, body, accent }) => (
            <div key={title} className="flex gap-4">
              <div className={`mt-0.5 flex-shrink-0 rounded-xl p-2.5 ring-1 ring-black/5 dark:ring-white/5 ${accent}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-950 dark:text-white">{title}</h3>
                <p className="mt-1 text-[15px] leading-relaxed text-gray-600 dark:text-gray-300">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURE COMPARISON TABLE ─────────────────────────── */}
      <section className="mx-auto max-w-[1200px] px-6 py-20">
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-950 dark:text-white">
            Compare every feature
          </h2>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
            A complete breakdown of what's included in each plan.
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse text-[15px]">

              {/* Plan name headers */}
              <thead>
                <tr className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
                  <th className="w-[300px] px-6 py-5 text-left text-[12px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Feature
                  </th>
                  {['Free', 'Pro', 'Team', 'Business', 'Enterprise'].map((planName, idx) => (
                    <th
                      key={planName}
                      className={`px-4 py-5 text-center text-base font-bold
                        ${idx === TEAM_COL
                          ? 'bg-indigo-50/70 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
                          : 'text-gray-800 dark:text-gray-200'
                        }`}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        {planName}
                        {idx === TEAM_COL && (
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-200 text-[10px] font-black text-indigo-700 dark:bg-indigo-800 dark:text-indigo-200">
                            ★
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {resolvedComparisonGroups.map((group) => (
                  <React.Fragment key={group.category}>
                    {/* Category header row */}
                    <tr className="border-t border-gray-100 dark:border-gray-800">
                      <td colSpan={6} className="bg-gray-50 px-6 py-3.5 dark:bg-gray-900/60">
                        <span className="text-[12px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                          {group.category}
                        </span>
                      </td>
                    </tr>

                    {/* Feature rows */}
                    {group.rows.map(({ label, values }) => (
                      <tr
                        key={label}
                        className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-900/30 transition-colors duration-100"
                      >
                        <td className="px-6 py-4 text-[15px] font-medium text-gray-800 dark:text-gray-200">
                          {label}
                        </td>
                        {values.map((value, idx) => (
                          <td
                            key={idx}
                            className={`px-4 py-4 ${idx === TEAM_COL ? 'bg-indigo-50/40 dark:bg-indigo-900/10' : ''}`}
                          >
                            <CellValue value={value} isHighlighted={idx === TEAM_COL} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>

            </table>
          </div>
        </div>
      </section>

      {/* ── WHY TEAMS UPGRADE ────────────────────────────────── */}
      <section className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/40 py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-gray-950 dark:text-white">Why teams upgrade</h2>
            <p className="mt-2 text-base text-gray-600 dark:text-gray-300">The clearest signal that it's time to move to the next plan.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { from: 'Free', to: 'Pro', reason: 'Need Kanban, Gantt, task dependencies, or more than 3 projects.' },
              { from: 'Pro', to: 'Team', reason: 'Ready for an org workspace, roles, departments, and shared calendars.' },
              { from: 'Team', to: 'Business', reason: 'Need custom permissions, full audit logs, or full data export.' },
              { from: 'Business', to: 'Enterprise', reason: 'SSO, SCIM, domain lock, procurement, SLA, or custom contracts.' },
            ].map(({ from, to, reason }) => (
              <div key={to} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm dark:from-gray-900 dark:to-gray-900/70">
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-600 dark:text-gray-300">{from}</span>
                  <FiArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-indigo-400" />
                  <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">{to}</span>
                </div>
                <p className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-950 dark:text-white">
            Frequently asked questions
          </h2>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
            Everything you need to know before choosing a plan.
          </p>
        </div>
        <div className="space-y-3">
          {faqs.map(({ q, a }) => (
            <FAQItem key={q} q={q} a={a} />
          ))}
        </div>
      </section>

      {/* ── ENTERPRISE CTA STRIP ─────────────────────────────── */}
      <section className="bg-gray-950 dark:bg-black">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-8 px-6 py-16 sm:flex-row sm:gap-10">
          <div className="flex-1 text-center sm:text-left">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gray-700 px-3.5 py-1 text-xs font-semibold text-gray-400">
              <FiBriefcase className="h-3.5 w-3.5 text-indigo-400" />
              Enterprise
            </div>
            <h2 className="text-2xl font-bold text-white">Building something bigger?</h2>
            <p className="mt-2 text-base leading-relaxed text-gray-400">
              SSO, SCIM provisioning, IP restrictions, domain lock, contractual SLA, and a dedicated Customer Success Manager. Let's talk.
            </p>
          </div>
          <div className="flex flex-shrink-0 gap-3">
            <button
              type="button"
              onClick={() => navigate('/contact')}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-6 text-sm font-semibold text-gray-950 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              Contact sales
              <FiArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <PublicPageFooter />
    </div>
  );
};

export default Pricing;
